import { compose, withProps, withPropsOnChange, withState } from "recompose";
import classNames from "classnames";
import { withDefaultProps } from "./";
import { withImmutableProps } from "../utils";
import isBefore from "date-fns/is_before";
import enhanceHeader from "../Header/withRange";
import format from "date-fns/format";
import parse from "date-fns/parse";
import styles from "../Day/Day.scss";
import moment from 'moment';
import _ from 'lodash';
import { addZero } from '../../../helpers/formatData';

let isTouchDevice = false;

export const EVENT_TYPE = {
    END: 3,
    HOVER: 2,
    START: 1
};

// Enhance Day component to display selected state based on an array of selected dates
export const enhanceDay = withPropsOnChange(["selected"], ({ date, selected, theme }) => {
    const isSelected = date >= selected.start && date <= selected.end;
    const isStart = date === selected.start;
    const isEnd = date === selected.end;
    const isRange = !(isStart && isEnd);
    const style = isRange && (
        isStart && { backgroundColor: theme.accentColor } ||
        isEnd && { borderColor: theme.accentColor }
    );

    return {
        className: isSelected && isRange && classNames(styles.range, {
            [styles.start]: isStart,
            [styles.betweenRange]: !isStart && !isEnd,
            [styles.end]: isEnd
        }),
        isSelected,
        selectionStyle: style
    };
});

// Enhancer to handle selecting and displaying multiple dates
export const withRange = compose(
    withDefaultProps,
    withState("scrollDate", "setScrollDate", getInitialDate),
    withState("displayKey", "setDisplayKey", getInitialDate),
    withState("selectionStart", "setSelectionStart", null),
    withImmutableProps(({
                            DayComponent,
                            HeaderComponent,
                            YearsComponent
                        }) => ({
        DayComponent: enhanceDay(DayComponent),
        HeaderComponent: enhanceHeader(HeaderComponent)
    })),
    withProps(({ displayKey, passThrough, selected, setDisplayKey, ...props }) => ({
        /* eslint-disable sort-keys */
        passThrough: {
            ...passThrough,
            Day: {
                onClick: (date) => handleSelect(date, { selected, ...props }),
                handlers: {
                    onMouseOver: !isTouchDevice && props.selectionStart
                        ? (e) => handleMouseOver(e, { selected, ...props })
                        : null
                }
            },
            Years: {
                selected: selected && selected[displayKey],
                onSelect: (date) => handleYearSelect(date, { displayKey, selected, ...props })
            },
            Header: {
                onYearClick: (date, e, key) => setDisplayKey(key || "start")
            }
        },
        selected: getSelectedData(selected)
    }))
);

function getSelectedData(selected) {
    const { start, end, eventType } = selected;

    /* There are two different ways the selected start and end times are coming through.
     *
     *  1. By default when the dates are loaded, we get the selected times as strings. They can directly
     *     be converted into moment since they are already formatted with the selected timezones.
     *  2. When we change dates in the custom calendar, we get a Date object. We need to call parseZone()
     *     to prevent converting the date into user localtime. This was causing the dates to get shifted by one day.
     *
     */

    let selectedStart = start;
    if (moment.isDate(start)) {
        selectedStart = moment.parseZone(start).format();
    }

    let selectedEnd = end;
    if (moment.isDate(end)) {
        selectedEnd = moment.parseZone(end).format();
    }

    const startInfoArray = _.get(moment(selectedStart), '_a');
    const endInfoArray = _.get(moment(selectedEnd), '_a')

    const startDate = `${_.get(startInfoArray, '[0]')}-${addZero(_.get(startInfoArray, '[1]') + 1)}-${addZero(_.get(startInfoArray, '[2]'))}`;
    const endDate = `${_.get(endInfoArray, '[0]')}-${addZero(_.get(endInfoArray, '[1]') + 1)}-${addZero(_.get(endInfoArray, '[2]'))}`;

    return {
        start: startDate,
        end: endDate,
        eventType: eventType || 0
    }
}

function getSortedSelection({ start, end }) {
    return isBefore(start, end)
        ? { start, end }
        : { start: end, end: start };
}

function handleSelect(date, { onSelect, selected, selectionStart, setSelectionStart }) {
    if (selectionStart) {
        onSelect({
            eventType: EVENT_TYPE.END,
            ...getSortedSelection({
                start: selectionStart,
                end: date
            })
        });
        setSelectionStart(null);
    } else {
        onSelect({ eventType: EVENT_TYPE.START, start: date, end: date });
        setSelectionStart(date);
    }
}

function handleMouseOver(e, { onSelect, selectionStart }) {
    const dateStr = e.target.getAttribute("data-date");
    const date = dateStr && parse(dateStr);

    if (!date) {
        return;
    }

    onSelect({
        eventType: EVENT_TYPE.HOVER,
        ...getSortedSelection({
            start: selectionStart,
            end: date
        })
    });
}

function handleYearSelect(date, { displayKey, onSelect, selected, setScrollDate }) {

    setScrollDate(date);
    onSelect(getSortedSelection(
        Object.assign({}, selected, { [displayKey]: parse(date) }))
    );
}

function getInitialDate({ selected }) {
    return selected && selected.start || new Date();
}

if (typeof window !== "undefined") {
    window.addEventListener("touchstart", function onTouch() {
        isTouchDevice = true;

        window.removeEventListener("touchstart", onTouch, false);
    });
}
